import { IconLibrary, SmartContentDisplay, Button } from '@act/storybook';
import {
  Box,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import HtmlDisplayer from 'react-components/components/html-displayer';
import NeedStatusTag from '../components/need-status-tag';
import NeedTimeAlert from './components/NeedTimeAlert';
import { GoalsSidePanel } from './GoalsSidePanel';
import { useNeedDetailState } from './hooks/use-need-detail-state';
import {
  NeedDetailDialogs,
  NeedDetailAdminMenu,
  NeedDetailHistorySection,
} from './components/need-detail-shared-components';
import syvaNeedsUtils from '../syvaNeeds.util';

export interface NeedDetailContentProps {
  needId: number;
  onClose: () => void;
  open: boolean;
  onAnimationComplete?: () => void;
}

export const NeedDetailContent = ({
  needId,
  onClose,
  open,
  onAnimationComplete,
}: NeedDetailContentProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const state = useNeedDetailState(needId);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
    }
  }, [open]);

  const handleAnimationComplete = () => {
    setShouldRender(false);
    onAnimationComplete?.();
  };

  if (!state.need) {
    return (
      <Box sx={{ padding: '24px', textAlign: 'center' }}>
        <Typography variant="bodyMedium" color="text.secondary">
          Need not found
        </Typography>
      </Box>
    );
  }

  if (!shouldRender) return null;

  const { need, issue, category, daysRemaining } = state;
  const remainingDaysDisplay =
    syvaNeedsUtils.formatRemainingDays(daysRemaining);
  return (
    <>
      <NeedDetailDialogs state={state} />
      {!state.editNeed &&
        !(state.isAddActionOpen || state.editAction) &&
        shouldRender && (
          <GoalsSidePanel
            open={open}
            animated={true}
            setShouldRenderSidePanel={handleAnimationComplete}
            titleContent={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px',
                  }}
                >
                  <Typography
                    variant="titleLarge"
                    sx={{
                      fontSize: isMobile ? '1.25rem' : undefined,
                      lineHeight: isMobile ? 1.3 : undefined,
                    }}
                  >
                    {issue?.label}
                  </Typography>
                  <NeedStatusTag need={need} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <NeedDetailAdminMenu state={state} />
                  <IconButton onClick={onClose}>
                    <IconLibrary iconName="X" size={24} weight="regular" />
                  </IconButton>
                </Box>
              </Box>
            }
            content={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  gap: '8px',
                }}
              >
                {need?.reason && (
                  <SmartContentDisplay maxHeight={150}>
                    <Typography variant="bodyMedium">
                      <HtmlDisplayer innerHtml={need?.reason} />
                    </Typography>
                  </SmartContentDisplay>
                )}
                <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '49px',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: theme.palette.navyShades[100],
                      marginBottom: '16px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <Typography variant="subheadMedium" fontWeight="semibold">
                        Category
                      </Typography>
                      <Typography variant="bodyMedium">
                        {category?.label}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <Typography variant="subheadMedium" fontWeight="semibold">
                        Remaining time
                      </Typography>
                      <Typography variant="bodyMedium">
                        {remainingDaysDisplay}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ marginBottom: '16px' }}>
                    <NeedTimeAlert need={need} showDaysRemaining={false} />
                  </Box>
                  <Divider sx={{ marginBottom: '16px' }} />
                  <NeedDetailHistorySection state={state} />
                </Box>
              </Box>
            }
            footerContent={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: isMobile ? 'center' : 'flex-end',
                  width: '100%',
                }}
              >
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    minWidth: isMobile ? '120px' : 'auto',
                    minHeight: '44px',
                    width: isMobile ? '100%' : 'auto',
                  }}
                >
                  Save & close
                </Button>
              </Box>
            }
          />
        )}
    </>
  );
};
